import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../components/ui/FormField';
import NumericInput from '../../components/NumericInput';
import type { SchemaFieldProps } from '../types';

/**
 * Composite value carried by the `entryCount` field for the entry membership
 * type. The entry UI owns both the entry count and the membership price
 * (formerly `yearlyPrice` labelled "Price"), so both travel through this one
 * field's value — the MembershipForm wrapper unpacks them into the separate
 * top-level payload keys (`entryCount`, `yearlyPrice`).
 */
export interface EntryConfigValue {
  entryCount: number;
  yearlyPrice: number;
}

/**
 * Custom SchemaForm widget for the membership `entryCount` field (entry type).
 * Extracted verbatim (behaviour-wise) from MembershipForm's former entry-count +
 * price inputs. Rebuilt with FormField + NumericInput (no raw inputs).
 */
const EntryConfigField: React.FC<SchemaFieldProps> = ({ field, value, onChange, error }) => {
  const { t } = useTranslation();
  const state: EntryConfigValue = value ?? { entryCount: 0, yearlyPrice: 0 };

  const patch = useCallback(
    (next: Partial<EntryConfigValue>) => {
      onChange({ ...state, ...next });
    },
    [onChange, state]
  );

  return (
    <>
      <FormField
        htmlFor="sf-entryCount"
        label={t('memberships.entryCount')}
        required={field.required}
        error={error}
        helperText={t('memberships.entryCountHelp')}
      >
        <NumericInput
          id="sf-entryCount"
          min="1"
          step="1"
          value={state.entryCount === 0 ? '' : state.entryCount}
          onChange={(e) => patch({ entryCount: e.target.value === '' ? 0 : parseInt(e.target.value, 10) })}
          required
        />
      </FormField>

      <FormField htmlFor="sf-yearlyPriceEntry" label={t('memberships.price')} required>
        <NumericInput
          id="sf-yearlyPriceEntry"
          min="0"
          step="1"
          value={state.yearlyPrice === 0 ? '' : state.yearlyPrice}
          onChange={(e) => patch({ yearlyPrice: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
          required
        />
      </FormField>
    </>
  );
};

export default EntryConfigField;
